import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useState, useEffect } from 'react'
import { useIsMobile } from '@/lib/hooks'
import { COLORS, FONTS, EASE_OUT, NEUTRAL2_RGB, sectionInner } from '@/components/home/tokens'
import { NEWS_ENTRIES, type NewsEntry } from '@/lib/newsEntries'

// Below this width the year + list can't sit side-by-side without crowding,
// so the layout stacks (year above the list). Above it, the list centers and
// the year sits to its left.
const STACK_BREAKPOINT = 1040

// Minimum left margin for the centered list on desktop. Holds the list clear of
// the gutter-aligned year marker, and makes the list eat into the clear space on
// the *right* first as the viewport narrows (rather than crowding the left).
const LIST_LEFT_MIN = 340

function useMaxWidth(maxPx: number) {
  const [matches, setMatches] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxPx}px)`)
    const update = () => setMatches(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [maxPx])
  return matches
}

export const Route = createFileRoute('/news')({
  component: NewsIndexPage,
})

const ASSET = (file: string) => `${import.meta.env.BASE_URL}images/news/${file}`

const DIVIDER_COLOR = `rgba(${NEUTRAL2_RGB}, 0.15)`

/** Sticky offset: clears the 88px fixed header plus a little breathing room.
 *  Matches the press page's sticky media-contact column. */
const STICKY_TOP = 112

function NewsRow({ entry, isMobile }: { entry: NewsEntry; isMobile: boolean }) {
  const [hovered, setHovered] = useState(false)

  return (
    <motion.a
      href={entry.href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={isMobile ? undefined : () => setHovered(true)}
      onMouseLeave={isMobile ? undefined : () => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column-reverse' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'stretch',
        gap: isMobile ? '18px' : '40px',
        padding: isMobile ? '28px 0' : '34px 0',
        textDecoration: 'none',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'space-between', gap: isMobile ? '14px' : 0, maxWidth: isMobile ? '100%' : '360px' }}>
        <motion.span
          animate={{ color: hovered ? COLORS.green1 : COLORS.neutral1 }}
          transition={{ duration: 0.3, ease: EASE_OUT }}
          style={{
            fontFamily: FONTS.body,
            fontWeight: 400,
            fontSize: 'var(--text-xl)',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
          }}
        >
          {entry.title}
        </motion.span>
        <span
          style={{
            fontFamily: FONTS.body,
            fontWeight: 400,
            fontSize: 'var(--text-sm)',
            color: COLORS.neutral5,
          }}
        >
          {entry.date}
        </span>
      </div>

      <div
        style={{
          flexShrink: 0,
          width: isMobile ? '100%' : '180px',
          height: isMobile ? undefined : '120px',
          aspectRatio: isMobile ? '180 / 110' : undefined,
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: `rgba(${NEUTRAL2_RGB}, 0.04)`,
        }}
      >
        <motion.img
          src={ASSET(entry.image)}
          alt={entry.alt}
          loading="lazy"
          animate={{ scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5, ease: EASE_OUT }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    </motion.a>
  )
}

const SR_ONLY: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
}

function YearMarker() {
  return (
    <motion.span
      aria-hidden
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE_OUT }}
      style={{
        display: 'block',
        fontFamily: FONTS.body,
        fontWeight: 800,
        fontSize: 'var(--text-6xl)',
        lineHeight: 1,
        letterSpacing: '-0.02em',
        color: `rgba(${NEUTRAL2_RGB}, 0.1)`,
      }}
    >
      2026
    </motion.span>
  )
}

function NewsList() {
  const isMobile = useIsMobile()
  return (
    <div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.9, ease: EASE_OUT }}
        style={{ height: '1px', backgroundColor: DIVIDER_COLOR, transformOrigin: 'left', marginBottom: '8px' }}
      />
      {NEWS_ENTRIES.map((entry, i) => (
        <motion.div
          key={entry.href}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 + i * 0.08, ease: EASE_OUT }}
        >
          <NewsRow entry={entry} isMobile={isMobile} />
        </motion.div>
      ))}
    </div>
  )
}

function NewsIndexPage() {
  const isMobile = useIsMobile()
  const isNarrow = useMaxWidth(STACK_BREAKPOINT)

  // Narrow (incl. mobile): year stacked above a full-width list — no room to
  // sit the year beside the list without crowding.
  if (isNarrow) {
    return (
      <section style={{ position: 'relative', zIndex: 10, padding: isMobile ? '120px 0 120px' : '160px 0 120px' }}>
        <div style={sectionInner}>
          <h1 style={SR_ONLY}>News (2026)</h1>
          <div style={{ marginBottom: '32px' }}>
            <YearMarker />
          </div>
          <NewsList />
        </div>
      </section>
    )
  }

  // Desktop: the list is centered on the screen via auto margins, but its left
  // margin is floored at LIST_LEFT_MIN. While there's slack the margins are
  // equal (centered); as the viewport narrows the floored left margin holds the
  // year's clearance and the auto right margin shrinks — so the clear space on
  // the right is consumed first. The year is pinned just left of the list and
  // sticks beneath the fixed header (mirrors the press page's sticky column).
  return (
    <section style={{ position: 'relative', zIndex: 10, padding: '180px 0 120px' }}>
      <h1 style={SR_ONLY}>News</h1>
      {/* Year — left-aligned to the site content gutter (same sectionInner edge
          as the body/footer across the site). The layer spans the list's height
          (the section's content box) so the inner block can stick beneath the
          fixed header as the list scrolls. */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: '180px', bottom: '120px', pointerEvents: 'none' }}>
        <div style={{ ...sectionInner, height: '100%' }}>
          <div style={{ position: 'sticky', top: STICKY_TOP, width: 'fit-content' }}>
            <YearMarker />
          </div>
        </div>
      </div>
      {/* List — centered on wide screens; floored left margin so the clear space
          on the right is consumed first as the viewport narrows. */}
      <div
        style={{
          position: 'relative',
          width: '660px',
          maxWidth: '100%',
          marginLeft: `max(${LIST_LEFT_MIN}px, calc((100% - 660px) / 2))`,
          marginRight: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <NewsList />
      </div>
    </section>
  )
}
